import React from 'react';
import { Input } from '@morressier/alembic';
import { Flex } from '@chakra-ui/react';
import { FieldLabel } from 'src/components/Field';
import { FieldError as IFieldError, useFormContext } from 'react-hook-form';
import _ from 'lodash';
import { FormFieldNumberInput as IFormFieldNumberInput } from '@publishing-engine/domain/types/article';
import FormFieldErrorMessage from './common/FormFieldErrorMessage';

interface IFormFieldNumberProps {
  config: IFormFieldNumberInput;
  isEditingAllowed?: boolean;
}

const FormFieldNumber: React.FC<IFormFieldNumberProps> = ({
  config,
  isEditingAllowed = true
}) => {
  const {
    params: { validations, labels },
    caseDataFields: { main: fieldPath }
  } = config;
  const minValue = validations.min ?? 0;
  const maxValue = validations.max;

  const {
    formState: { errors },
    register
  } = useFormContext();

  const error = _.get(errors, fieldPath) as IFieldError;

  return (
    <Flex flexDirection="column" gap={2} alignItems="flex-start">
      <FieldLabel
        label={labels.main}
        subText={labels.subtitle}
        isRequired={validations.required}
        helpText={labels.helpText}
      />
      <Flex flexDirection="column" flex={1} width={'100%'} gap={2}>
        <Input
          type="number"
          defaultValue={''} // important to avoid uncontrolled warning
          min={minValue}
          max={maxValue}
          {...register(fieldPath, {
            required: validations.required,
            min: {
              value: minValue,
              message: `Value must be ${minValue} or greater`
            },
            ...(maxValue !== undefined && {
              max: {
                value: maxValue,
                message: `Value must be ${maxValue} or less`
              }
            }),
            // convert input manually on change to avoid NaN
            setValueAs: value => {
              if (['', null].includes(value)) return null;
              const num = Number(value);
              return isNaN(num) ? null : num; // avoid NaN
            }
          })}
          isInvalid={Boolean(error)}
          placeholder={labels.placeholder}
          isReadOnly={!isEditingAllowed}
        />
        <FormFieldErrorMessage config={config} />
      </Flex>
    </Flex>
  );
};

export default FormFieldNumber;
